import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ErrorWarningCircle, Info, Success } from '../../assets/icons';
import { IconSize } from '../../constant/IconSize.constant';
import { useCompany } from '../../hooks/useCompany';
import { useFetchLicenseSelfServeRenewableListQuery } from '../../services/license.api';
import { SCOPE_TYPE } from '../../services/utils';
import { convertToLocaleCurrency } from '../../utils/Currency.Util';
import { convertToDate, formatDate } from '../../utils/Date.Util';
import { Card, ColorType, Icon, Label, LabelType } from '../_ui';
import { Tooltip, TooltipContent, TooltipTrigger } from '../_ui/Tooltip.component';
import SelfServeStartRenewal from './SelfServeStartRenewal.component';

export const SelfServiceRenewablesSummary = () => {
  const { t } = useTranslation();
  const { userScope, companyId } = useCompany();
  const { data: renewableData } = useFetchLicenseSelfServeRenewableListQuery({
    scope: userScope || SCOPE_TYPE.COMPANY,
    companyId: companyId || '',
    limit: 100,
  });

  const NoRenewables = () => {
    return (
      <Card className='py-4 mb-6'>
        <div className='flex flex-col gap-3'>
          <div className='flex flex-row items-center gap-2'>
            <Icon size={IconSize.SIZE_24x24} src={Success} color={ColorType.POSITIVE2} />
            <Label text={t('license_up_to_date')} type={LabelType.H4} color={ColorType.BLACK} />
          </div>
        </div>
      </Card>
    );
  };

  const renewableSummary = useMemo(() => {
    const totalCount = renewableData?.totalCount;
    const earliestExpirationDate = renewableData?.entities?.at(0)?.expirationDate;
    const currency = renewableData?.entities?.at(0)?.renewalOptions?.at(0)?.currency || 'USD';
    const totalAmountCents =
      renewableData?.entities?.reduce((accumulator, currentItem) => {
        return accumulator + (currentItem.renewalOptions?.at(0)?.amountCents || 0);
      }, 0) || 0;
    return {
      totalCount,
      earliestExpirateDate: earliestExpirationDate,
      totalAmountCents,
      currency,
    };
  }, [renewableData]);
  const data = renewableData?.entities || [];
  const earliestExpirationDate = renewableSummary.earliestExpirateDate
    ? formatDate(convertToDate(renewableSummary.earliestExpirateDate), 'MMM d, yyyy')
    : undefined;
  const totalAmountDollars = convertToLocaleCurrency(
    renewableSummary.totalAmountCents / 100,
    renewableSummary.currency,
  );
  // don't allow self serve renewal if total amount exceeeds $5,000
  const MAX_AMOUNT_CENTS_ALLOWED = 500000;
  const isSelfServeAvailable = renewableSummary?.totalAmountCents <= MAX_AMOUNT_CENTS_ALLOWED;

  // hide the summary when it's loading, or there are eligible renewable licenses but no renewal options availabe for them
  const hideSummary =
    renewableSummary?.totalCount === undefined ||
    (renewableSummary.totalCount > 0 && renewableSummary.totalAmountCents === 0);

  return hideSummary ? null : renewableSummary.totalCount === 0 ? (
    <NoRenewables />
  ) : (
    <Card className='py-4 mb-6'>
      <div className='flex flex-col gap-3'>
        <div className='flex flex-row items-center gap-2'>
          <Icon size={IconSize.SIZE_24x24} src={ErrorWarningCircle} color={ColorType.WARNING2} />
          <Label
            text={t('license_expiring_soon', {
              count: renewableSummary.totalCount,
            })}
            type={LabelType.H4}
            color={ColorType.BLACK}
          />
        </div>
        <Label
          htmlText={t(
            isSelfServeAvailable
              ? 'license_expiring_soon_subtitle'
              : 'license_expiring_soon_contact_sales',
            { count: renewableSummary.totalCount },
          )}
          type={LabelType.BODY3}
          color={ColorType.GREY5}
        />
        <Card className='border-2 border-grey2'>
          <div className='flex flex-row items-center justify-between'>
            <div className='flex flex-row items-center gap-4 divide-x divide-grey3'>
              <div>
                <Label
                  text={t('license_expiring_in_days')}
                  type={LabelType.BODY3}
                  color={ColorType.GREY5}
                />
                <Label
                  text={renewableSummary.totalCount}
                  type={LabelType.H3}
                  color={ColorType.BLACK}
                  className='mt-1'
                />
              </div>
              <div className='pl-5 gap-2'>
                <Label
                  text={t('next_license_expire_date')}
                  type={LabelType.BODY3}
                  color={ColorType.GREY5}
                />
                <Label
                  text={earliestExpirationDate}
                  type={LabelType.H3}
                  color={ColorType.BLACK}
                  className='mt-1'
                />
              </div>
              <div className='pl-5 gap-2'>
                <div className='flex flex-row'>
                  <Label
                    text={t('estimated_cost')}
                    type={LabelType.BODY3}
                    color={ColorType.GREY5}
                  />
                  <Tooltip>
                    <TooltipTrigger>
                      <Icon src={Info} size={IconSize.SIZE_16x16} className='ml-1' />
                    </TooltipTrigger>
                    <TooltipContent>
                      <span
                        className='text-sm text-white font-medium'
                        style={{ fontFamily: 'Inter' }}
                      >
                        <Trans
                          i18nKey='estimated_cost_info'
                          values={{ count: renewableSummary.totalCount }}
                          components={{
                            br: <br />,
                          }}
                        />
                      </span>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className='flex flex-row items-center mt-1'>
                  <Label text={totalAmountDollars} type={LabelType.H3} color={ColorType.BLACK} />
                  <div style={{ alignSelf: 'flex-end', padding: '4px' }}>
                    <Label
                      text={renewableSummary.currency}
                      type={LabelType.BODY3}
                      color={ColorType.GREY5}
                    />
                  </div>
                </div>
              </div>
            </div>
            {isSelfServeAvailable && (
              <div className='flex justify-end gap-4 xl:gap-6'>
                <SelfServeStartRenewal renewables={data} />
              </div>
            )}
          </div>
        </Card>
      </div>
    </Card>
  );
};

export default SelfServiceRenewablesSummary;
